/**
 * Dual Metrics Panel Component
 *
 * Side-by-side bar charts comparing original vs enhanced page metrics.
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { MetricsSet, CompetitorMetrics } from '@/services/aeoDemoService';

interface DualMetricsPanelProps {
  baseline: MetricsSet | null;
  enhanced: MetricsSet | null;
  competitor?: CompetitorMetrics | null;
  isCalculating?: boolean;
}

const METRIC_LABELS: Record<string, string> = {
  // AEO
  intent_coverage: 'Intent Coverage',
  faq_quality: 'FAQ Quality',
  entity_richness: 'Entity Richness',
  readability: 'Readability',
  conversational_usability: 'Conversational',
  // SEO
  schema_coverage: 'Schema Coverage',
  meta_optimization: 'Meta Optimization',
  internal_linking: 'Internal Linking',
  content_depth: 'Content Depth',
  keyword_coverage: 'Keyword Coverage',
  // UX
  ctas: 'CTAs',
  trust_elements: 'Trust Elements',
  media_quality: 'Media Quality',
  comparison_clarity: 'Comparison Clarity',
  // Scientific
  semanticDensity: 'Semantic Density',
  answerCoverage: 'Answer Coverage',
  citationReadiness: 'Citation Readiness',
  specCompleteness: 'Spec Completeness',
};

const CATEGORY_COLORS = {
  aeo: { original: '#94a3b8', enhanced: '#3b82f6', competitor: '#ef4444' },
  seo: { original: '#94a3b8', enhanced: '#10b981', competitor: '#ef4444' },
  ux: { original: '#94a3b8', enhanced: '#f59e0b', competitor: '#ef4444' },
  scientific: { original: '#94a3b8', enhanced: '#8b5cf6', competitor: '#ef4444' },
};

function CustomLegend({
  colors,
  hasEnhanced,
  hasCompetitor
}: {
  colors: typeof CATEGORY_COLORS.aeo;
  hasEnhanced: boolean;
  hasCompetitor: boolean;
}) {
  return (
    <div className="flex items-center justify-center gap-6 mt-2">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: colors.original }} />
        <span className="text-xs text-muted-foreground">Your Page</span>
      </div>
      {hasEnhanced && (
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: colors.enhanced }} />
          <span className="text-xs text-muted-foreground">With AEO</span>
        </div>
      )}
      {hasCompetitor && (
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: colors.competitor }} />
          <span className="text-xs text-muted-foreground">Competitor</span>
        </div>
      )}
    </div>
  );
}

function formatMetricValue(value: number, isScientific: boolean = false): number {
  return isScientific ? Math.round(value * 100) : Math.round(value);
}

function getDelta(original: number, enhanced: number | undefined): number | null {
  if (enhanced === undefined) return null;
  return Math.round(enhanced - original);
}

function DeltaIndicator({ delta }: { delta: number | null }) {
  if (delta === null) return null;

  if (delta > 0) {
    return (
      <span className="flex items-center text-green-600 text-xs">
        <TrendingUp className="w-3 h-3 mr-1" />
        +{delta}
      </span>
    );
  }
  if (delta < 0) {
    return (
      <span className="flex items-center text-red-600 text-xs">
        <TrendingDown className="w-3 h-3 mr-1" />
        {delta}
      </span>
    );
  }
  return (
    <span className="flex items-center text-muted-foreground text-xs">
      <Minus className="w-3 h-3 mr-1" />
      0
    </span>
  );
}

function MetricsChart({
  category,
  baseline,
  enhanced,
  competitor,
  isScientific = false,
}: {
  category: 'aeo' | 'seo' | 'ux' | 'scientific';
  baseline: Record<string, number>;
  enhanced: Record<string, number> | null;
  competitor?: Record<string, number> | null;
  isScientific?: boolean;
}) {
  const colors = CATEGORY_COLORS[category];

  const data = Object.entries(baseline).map(([key, value]) => {
    const baseValue = formatMetricValue(value, isScientific);
    const enhancedValue = enhanced
      ? formatMetricValue(enhanced[key], isScientific)
      : null;
    const competitorValue = competitor
      ? formatMetricValue(competitor[key], isScientific)
      : null;

    return {
      name: METRIC_LABELS[key] || key,
      original: baseValue,
      enhanced: enhancedValue,
      competitor: competitorValue,
      delta: enhancedValue !== null ? enhancedValue - baseValue : null,
    };
  });

  return (
    <div className="space-y-1">
      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
            <XAxis
              type="number"
              domain={[0, 100]}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              tickLine={{ stroke: 'hsl(var(--border))' }}
            />
            <YAxis
              dataKey="name"
              type="category"
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              tickLine={{ stroke: 'hsl(var(--border))' }}
              width={95}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                color: 'hsl(var(--card-foreground))',
              }}
              labelStyle={{
                color: 'hsl(var(--card-foreground))',
                fontWeight: 600,
              }}
              itemStyle={{
                color: 'hsl(var(--card-foreground))',
              }}
              formatter={(value: number, name: string) => [
                `${value}%`,
                name === 'original' ? 'Your Page' : name === 'enhanced' ? 'With AEO' : 'Competitor',
              ]}
            />
            <Bar
              dataKey="original"
              fill={colors.original}
              name="Your Page"
              radius={[0, 4, 4, 0]}
            />
            {enhanced && (
              <Bar
                dataKey="enhanced"
                fill={colors.enhanced}
                name="With AEO"
                radius={[0, 4, 4, 0]}
              />
            )}
            {competitor && (
              <Bar
                dataKey="competitor"
                fill={colors.competitor}
                name="Competitor"
                radius={[0, 4, 4, 0]}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <CustomLegend
        colors={colors}
        hasEnhanced={!!enhanced}
        hasCompetitor={!!competitor}
      />
    </div>
  );
}

function OverallScoreCard({
  label,
  original,
  enhanced,
  color,
}: {
  label: string;
  original: number;
  enhanced: number | null;
  color: string;
}) {
  const delta = enhanced !== null ? Math.round(enhanced - original) : null;

  return (
    <div className="text-center p-3 rounded-lg bg-muted/30">
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="flex items-center justify-center gap-2">
        <span className="text-lg font-semibold text-muted-foreground">
          {Math.round(original)}
        </span>
        {enhanced !== null && (
          <>
            <span className="text-muted-foreground">→</span>
            <span
              className="text-lg font-semibold"
              style={{ color: delta !== null && delta > 0 ? color : undefined }}
            >
              {Math.round(enhanced)}
            </span>
          </>
        )}
      </div>
      {delta !== null && (
        <DeltaIndicator delta={delta} />
      )}
    </div>
  );
}

export function DualMetricsPanel({
  baseline,
  enhanced,
  competitor,
  isCalculating,
}: DualMetricsPanelProps) {
  if (!baseline) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          No baseline metrics available
        </CardContent>
      </Card>
    );
  }

  const competitorAeo = competitor?.metrics?.aeo;
  const competitorSeo = competitor?.metrics?.seo;
  const competitorUx = competitor?.metrics?.ux;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Metrics Comparison</CardTitle>
          <div className="flex items-center gap-2">
            {competitor && (
              <Badge variant="outline" className="text-xs">
                vs {competitor.name}
              </Badge>
            )}
            {isCalculating && (
              <Badge variant="outline" className="animate-pulse">
                Calculating...
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Overall Scores */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <OverallScoreCard
            label="AEO Score"
            original={baseline.overall.aeo}
            enhanced={enhanced?.overall.aeo ?? null}
            color="#3b82f6"
          />
          <OverallScoreCard
            label="SEO Score"
            original={baseline.overall.seo}
            enhanced={enhanced?.overall.seo ?? null}
            color="#10b981"
          />
          <OverallScoreCard
            label="UX Score"
            original={baseline.overall.ux}
            enhanced={enhanced?.overall.ux ?? null}
            color="#f59e0b"
          />
          <OverallScoreCard
            label="Overall"
            original={baseline.overall.total}
            enhanced={enhanced?.overall.total ?? null}
            color="#8b5cf6"
          />
        </div>

        {/* Detailed Metrics Tabs */}
        <Tabs defaultValue="aeo" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="aeo">AEO</TabsTrigger>
            <TabsTrigger value="seo">SEO</TabsTrigger>
            <TabsTrigger value="ux">UX</TabsTrigger>
            <TabsTrigger value="scientific">Scientific</TabsTrigger>
          </TabsList>

          <TabsContent value="aeo" className="mt-4">
            <MetricsChart
              category="aeo"
              baseline={baseline.aeo}
              enhanced={enhanced?.aeo ?? null}
              competitor={competitorAeo}
            />
          </TabsContent>

          <TabsContent value="seo" className="mt-4">
            <MetricsChart
              category="seo"
              baseline={baseline.seo}
              enhanced={enhanced?.seo ?? null}
              competitor={competitorSeo}
            />
          </TabsContent>

          <TabsContent value="ux" className="mt-4">
            <MetricsChart
              category="ux"
              baseline={baseline.ux}
              enhanced={enhanced?.ux ?? null}
              competitor={competitorUx}
            />
          </TabsContent>

          <TabsContent value="scientific" className="mt-4">
            <MetricsChart
              category="scientific"
              baseline={baseline.scientific}
              enhanced={enhanced?.scientific ?? null}
              isScientific
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

export default DualMetricsPanel;
